import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Activity, Globe, PenSquare, AlertTriangle, TrendingUp, Settings, LayoutGrid, Library, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react'
import { usePulseContext } from '@/contexts/PulseContext'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// Constants for sidebar dimensions
const SIDEBAR_MIN_WIDTH = 72 // Collapsed width (icons only)
const SIDEBAR_DEFAULT_WIDTH = 288 // 18rem = 288px
const SIDEBAR_MAX_WIDTH = 400
const SIDEBAR_STORAGE_KEY = 'rsb-sidebar-width'
const SIDEBAR_COLLAPSED_KEY = 'rsb-sidebar-collapsed'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  href: string
  icon: string
  shortcut?: string
  badge?: string | number
}

interface NavSectionProps {
  title: string
  items: NavItem[]
  onClose: () => void
  isActiveRoute: (href: string) => boolean
  getNavIcon: (iconName: string, active: boolean) => React.ReactNode
  isCollapsed: boolean
}

function NavSection({ title, items, onClose, isActiveRoute, getNavIcon, isCollapsed }: NavSectionProps) {
  return (
    <div className={cn("space-y-2 mb-8", isCollapsed && "mb-6")}>
      {!isCollapsed && (
        <h3 className="px-4 text-[10px] font-display font-bold tracking-[0.2em] text-rsb-muted/60 uppercase animate-in fade-in duration-300">
          {title}
        </h3>
      )}
      <nav className={cn("space-y-1", isCollapsed ? "px-1" : "px-2")} aria-label={title}>
        {items.map((item, index) => {
          const active = isActiveRoute(item.href)
          const navLink = (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              aria-current={active ? 'page' : undefined}
              style={{
                animationDelay: `${index * 30}ms`,
              }}
              className={cn(
                "group relative flex items-center rounded-xl transition-all duration-300",
                "animate-in slide-in-from-left-2 fade-in",
                "hover:scale-[1.02] active:scale-[0.98]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent focus-visible:ring-offset-2 focus-visible:ring-offset-rsb-bg",
                isCollapsed
                  ? "justify-center p-3"
                  : "gap-3 px-3 py-2.5",
                active
                  ? 'bg-white/10 shadow-[0_0_20px_rgba(124,58,237,0.15)] border border-white/10'
                  : 'hover:bg-white/5 border border-transparent'
              )}
            >
              {/* Active indicator bar */}
              {active && (
                <div
                  className={cn(
                    "absolute left-0 w-1 bg-rsb-accent rounded-r-full shadow-[0_0_10px_#7C3AED]",
                    "transition-all duration-300",
                    isCollapsed ? "h-4" : "h-6"
                  )}
                />
              )}

              {/* Icon with hover animation */}
              <span className={cn(
                "flex-shrink-0 relative z-10 transition-transform duration-200",
                "group-hover:scale-110"
              )}>
                {getNavIcon(item.icon, active)}
              </span>

              {/* Label and badges - only when expanded */}
              {!isCollapsed && (
                <div className="flex-1 min-w-0 flex items-center justify-between relative z-10 animate-in fade-in slide-in-from-left-2 duration-200">
                  <span className={cn(
                    "text-sm font-medium transition-colors",
                    active ? 'text-white' : 'text-rsb-text-secondary group-hover:text-white'
                  )}>
                    {item.label}
                  </span>
                  <div className="flex items-center gap-2">
                    {item.badge && (
                      <span className="px-1.5 py-0.5 bg-rsb-accent text-white text-[10px] font-bold rounded shadow-glow-sm animate-pulse">
                        {item.badge}
                      </span>
                    )}
                    {item.shortcut && (
                      <span className="hidden lg:block text-[10px] font-mono text-rsb-muted/50 group-hover:text-rsb-muted transition-colors">
                        {item.shortcut}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Link>
          )

          // Wrap in tooltip when collapsed
          if (isCollapsed) {
            return (
              <TooltipProvider key={item.href} delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {navLink}
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="glass-panel border-white/10 bg-rsb-surface/95 text-white px-3 py-2"
                    sideOffset={8}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="px-1.5 py-0.5 bg-rsb-accent text-white text-[10px] font-bold rounded">
                          {item.badge}
                        </span>
                      )}
                      {item.shortcut && (
                        <span className="text-[10px] font-mono text-rsb-muted">
                          {item.shortcut}
                        </span>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          }

          return navLink
        })}
      </nav>
    </div>
  )
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const resizeHandleRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Load persisted state from localStorage
  const [width, setWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
      return stored ? parseInt(stored, 10) : SIDEBAR_DEFAULT_WIDTH
    }
    return SIDEBAR_DEFAULT_WIDTH
  })

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      return stored === 'true'
    }
    return false
  })

  const [isResizing, setIsResizing] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  const { data: pulseData } = usePulseContext()

  const healthScore = pulseData?.stats?.health_score ?? 0.75
  const pendingEmergences = pulseData?.emergences?.length ?? 0

  // Persist width to localStorage
  useEffect(() => {
    if (!isCollapsed) {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, width.toString())
    }
  }, [width, isCollapsed])

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, isCollapsed.toString())
  }, [isCollapsed])

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = Math.max(
        SIDEBAR_MIN_WIDTH + 50, // Don't allow manual resize to collapse
        Math.min(e.clientX - 16, SIDEBAR_MAX_WIDTH) // Account for left margin
      )
      setWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  // Toggle collapse
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [])

  const mainNavItems: NavItem[] = [
    {
      label: 'Pulse',
      href: '/',
      icon: 'pulse',
      shortcut: '⌘1',
      badge: pendingEmergences > 0 ? pendingEmergences : undefined
    },
    {
      label: 'Constellation',
      href: '/constellation',
      icon: 'network',
      shortcut: '⌘2'
    },
    {
      label: 'Editor',
      href: '/editor',
      icon: 'edit',
      shortcut: '⌘3'
    },
    {
      label: 'Conflitos',
      href: '/conflicts',
      icon: 'alert'
    },
    {
      label: 'Evolucao',
      href: '/evolution',
      icon: 'trending'
    },
    {
      label: 'Canvas',
      href: '/canvas',
      icon: 'canvas',
      shortcut: '⌘4'
    },
    {
      label: 'Library',
      href: '/library',
      icon: 'library',
      shortcut: '⌘5'
    }
  ]

  const settingsNavItems: NavItem[] = [
    {
      label: 'Configuracoes',
      href: '/settings',
      icon: 'settings'
    }
  ]

  const isActiveRoute = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const getHealthScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-emerald-400 drop-shadow-md'
    if (score >= 0.6) return 'text-yellow-400 drop-shadow-md'
    return 'text-red-400 drop-shadow-md'
  }

  const getNavIcon = (iconName: string, active: boolean) => {
    const className = cn(
      "w-5 h-5 transition-all duration-300",
      active
        ? 'text-white filter drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]'
        : 'text-rsb-text-secondary group-hover:text-white'
    )

    const icons: { [key: string]: React.ReactNode } = {
      pulse: <Activity className={className} />,
      network: <Globe className={className} />,
      edit: <PenSquare className={className} />,
      alert: <AlertTriangle className={className} />,
      trending: <TrendingUp className={className} />,
      canvas: <LayoutGrid className={className} />,
      library: <Library className={className} />,
      settings: <Settings className={className} />,
    }
    return icons[iconName] || icons.pulse
  }

  // Calculate actual display width
  const displayWidth = isCollapsed ? SIDEBAR_MIN_WIDTH : width

  return (
    <>
      <div
        ref={sidebarRef}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ width: displayWidth }}
        className={cn(
          "fixed top-0 bottom-0 left-0 z-40 pointer-events-none flex flex-col justify-center",
          "lg:left-4",
          "transition-all duration-500 ease-out",
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className={cn(
          "pointer-events-auto w-full h-[90vh] glass-panel rounded-2xl flex flex-col shadow-2xl overflow-hidden relative",
          "transition-all duration-300 ease-out"
        )}>

          {/* Noise overlay */}
          <div className="absolute inset-0 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />

          {/* Collapse toggle button */}
          <button
            onClick={toggleCollapse}
            className={cn(
              "absolute -right-3 top-1/2 -translate-y-1/2 z-20",
              "w-6 h-12 rounded-full bg-rsb-surface border border-white/10",
              "flex items-center justify-center",
              "transition-all duration-300",
              "hover:bg-rsb-surface-elevated hover:border-rsb-accent/50 hover:scale-110",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent",
              "opacity-0 lg:opacity-100",
              isHovering || isCollapsed ? "opacity-100" : "lg:opacity-0 lg:hover:opacity-100"
            )}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-rsb-text-secondary" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-rsb-text-secondary" />
            )}
          </button>

          {/* Header */}
          <div className={cn(
            "relative z-10 transition-all duration-300",
            isCollapsed ? "p-3 pb-2" : "p-6 pb-2"
          )}>
            <div className={cn(
              "flex items-center mb-6 transition-all duration-300",
              isCollapsed ? "justify-center" : "gap-3"
            )}>
              <div className={cn(
                "rounded-lg bg-gradient-to-br from-rsb-accent to-rsb-accent-glow flex items-center justify-center shadow-glow-md",
                "transition-all duration-300 hover:scale-105",
                isCollapsed ? "w-10 h-10" : "w-8 h-8"
              )}>
                <span className={cn(
                  "font-display font-bold text-white",
                  isCollapsed ? "text-base" : "text-sm"
                )}>SB</span>
              </div>
              {!isCollapsed && (
                <div className="animate-in fade-in slide-in-from-left-2 duration-300">
                  <h1 className="font-display font-bold text-lg text-white leading-none tracking-tight">SECOND BRAIN</h1>
                  <p className="text-[10px] text-rsb-text-secondary tracking-widest uppercase mt-1">Cognitive OS v2.0</p>
                </div>
              )}
            </div>

            {/* Health Stats */}
            <div className={cn(
              "rounded-xl bg-white/5 border border-white/5 relative overflow-hidden group",
              "transition-all duration-300 hover:bg-white/8 hover:border-white/10",
              isCollapsed ? "p-2" : "p-4"
            )}>
              <div className="absolute inset-0 bg-gradient-to-r from-rsb-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {isCollapsed ? (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-center gap-1 relative z-10 cursor-default">
                        <span className={cn("text-lg font-bold font-mono", getHealthScoreColor(healthScore))}>
                          {Math.round(healthScore * 100)}
                        </span>
                        <div className="w-full">
                          <Progress
                            value={healthScore * 100}
                            className={cn(
                              "h-1 bg-black/40 [&>div]:shadow-[0_0_10px_currentColor]",
                              healthScore > 0.8
                                ? '[&>div]:bg-emerald-500'
                                : healthScore > 0.6
                                  ? '[&>div]:bg-yellow-500'
                                  : '[&>div]:bg-red-500'
                            )}
                          />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="glass-panel border-white/10 bg-rsb-surface/95 text-white"
                      sideOffset={8}
                    >
                      <span className="font-medium">Neural Health: {Math.round(healthScore * 100)}%</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <span className="text-xs font-medium text-rsb-text-secondary">Neural Health</span>
                    <span className={cn("text-xs font-bold font-mono", getHealthScoreColor(healthScore))}>
                      {Math.round(healthScore * 100)}%
                    </span>
                  </div>
                  <Progress
                    value={healthScore * 100}
                    className={cn(
                      "h-1.5 bg-black/40 relative z-10 [&>div]:shadow-[0_0_10px_currentColor]",
                      "[&>div]:transition-all [&>div]:duration-500",
                      healthScore > 0.8
                        ? '[&>div]:bg-emerald-500'
                        : healthScore > 0.6
                          ? '[&>div]:bg-yellow-500'
                          : '[&>div]:bg-red-500'
                    )}
                  />
                </>
              )}
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className={cn(
            "flex-1 relative z-10 transition-all duration-300",
            isCollapsed ? "py-4" : "py-6"
          )}>
            <NavSection
              title="System"
              items={mainNavItems}
              onClose={onClose}
              isActiveRoute={isActiveRoute}
              getNavIcon={getNavIcon}
              isCollapsed={isCollapsed}
            />
            <NavSection
              title="Settings"
              items={settingsNavItems}
              onClose={onClose}
              isActiveRoute={isActiveRoute}
              getNavIcon={getNavIcon}
              isCollapsed={isCollapsed}
            />
          </ScrollArea>

          {/* Footer */}
          <div className={cn(
            "border-t border-white/5 bg-black/20 relative z-10",
            "transition-all duration-300",
            isCollapsed ? "p-2" : "p-4"
          )}>
            {isCollapsed ? (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-default">
                      <div className="text-sm font-bold text-white font-mono">
                        {pulseData?.stats?.total_concepts ?? '-'}
                      </div>
                      <div className="text-[8px] text-rsb-muted uppercase">C</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="glass-panel border-white/10 bg-rsb-surface/95 text-white"
                    sideOffset={8}
                  >
                    <div className="space-y-1">
                      <div className="flex justify-between gap-4">
                        <span className="text-rsb-text-secondary">Concepts:</span>
                        <span className="font-mono font-bold">{pulseData?.stats?.total_concepts ?? '-'}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-rsb-text-secondary">Links:</span>
                        <span className="font-mono font-bold">{pulseData?.stats?.total_connections ?? '-'}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-rsb-text-secondary">Inputs:</span>
                        <span className="font-mono font-bold">{pulseData?.stats?.total_ingestions ?? '-'}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div className="grid grid-cols-3 gap-2 mb-4 animate-in fade-in duration-300">
                <div className="text-center p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-105">
                  <div className="text-xs font-bold text-white font-mono">
                    {pulseData?.stats?.total_concepts ?? '-'}
                  </div>
                  <div className="text-[9px] text-rsb-muted uppercase mt-0.5">Concepts</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-105">
                  <div className="text-xs font-bold text-white font-mono">
                    {pulseData?.stats?.total_connections ?? '-'}
                  </div>
                  <div className="text-[9px] text-rsb-muted uppercase mt-0.5">Links</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-105">
                  <div className="text-xs font-bold text-white font-mono">
                    {pulseData?.stats?.total_ingestions ?? '-'}
                  </div>
                  <div className="text-[9px] text-rsb-muted uppercase mt-0.5">Inputs</div>
                </div>
              </div>
            )}
          </div>

          {/* Resize handle - only visible when expanded and on desktop */}
          {!isCollapsed && (
            <div
              ref={resizeHandleRef}
              onMouseDown={handleMouseDown}
              className={cn(
                "absolute top-0 right-0 w-1 h-full cursor-col-resize z-30",
                "hidden lg:block",
                "group/resize",
                "hover:bg-rsb-accent/30 transition-colors duration-200",
                isResizing && "bg-rsb-accent/50"
              )}
            >
              {/* Visual handle indicator */}
              <div className={cn(
                "absolute top-1/2 -translate-y-1/2 right-0 w-3 h-8",
                "flex items-center justify-center",
                "opacity-0 group-hover/resize:opacity-100 transition-opacity",
                isResizing && "opacity-100"
              )}>
                <GripVertical className="w-3 h-3 text-rsb-accent" />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
